import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

const AutocompleteInput = ({
    value,
    onChange,
    suggestions = [],
    placeholder = '',
    className = '',
    colorIndicator = null
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const [dropdownPosition, setDropdownPosition] = useState({});

    const filteredSuggestions = value
        ? (suggestions || []).filter(suggestion =>
            suggestion.toLowerCase().includes(value.toLowerCase())
        )
        : (suggestions || []);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width,
            });
        }
    }, [isOpen]);

    useEffect(() => {
        setHighlightedIndex(-1);
    }, [value, suggestions]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                inputRef.current &&
                !inputRef.current.contains(event.target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleKeyDown = (e) => {
        if (filteredSuggestions.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (!isOpen) setIsOpen(true);
                setHighlightedIndex(prev =>
                    prev < filteredSuggestions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (!isOpen) setIsOpen(true);
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                if (isOpen && highlightedIndex >= 0) {
                    e.preventDefault();
                    handleSelect(filteredSuggestions[highlightedIndex]);
                }
                break;
            case 'Tab':
                if (isOpen) {
                    e.preventDefault(); // Take control of the Tab key
                    if (highlightedIndex >= 0) {
                        handleSelect(filteredSuggestions[highlightedIndex]);
                    } else {
                        setIsOpen(false); // No item selected, just close the dropdown
                    }
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setHighlightedIndex(-1);
                break;
            default:
                break;
        }
    };

    const handleSelect = (suggestion) => {
        onChange(suggestion);
        inputRef.current?.focus(); // Set focus back to the input
        setTimeout(() => {
            setIsOpen(false);
            setHighlightedIndex(-1);
        }, 0);
    };

    const dropdown = isOpen && filteredSuggestions.length > 0 && createPortal(
        <div
            ref={dropdownRef}
            className="absolute z-50 w-full mt-1 bg-[#2d3e50] border-2 border-[#3d4f61] rounded-lg shadow-xl max-h-48 overflow-y-auto"
            style={{
                position: 'absolute',
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
            }}
        >
            {filteredSuggestions.map((suggestion, index) => (
                <button
                    key={index}
                    type="button"
                    onClick={() => handleSelect(suggestion)}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${index === highlightedIndex
                        ? 'bg-amber-500/20 text-amber-300'
                        : 'text-gray-300 hover:bg-white/5'
                        }`}
                >
                    {suggestion}
                </button>
            ))}
        </div>,
        document.body
    );

    return (
        <>
            <div className="relative">
                {colorIndicator && (
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 sm:pl-3.5">
                        <span
                            className="h-2.5 w-2.5 rounded-full"
                            style={{ backgroundColor: colorIndicator }}
                        />
                    </div>
                )}
                <input
                    ref={inputRef}
                    type="text"
                    value={value}
                    onChange={(e) => {
                        onChange(e.target.value);
                        setIsOpen(true);
                    }}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setIsOpen(true)}
                    placeholder={placeholder}
                    className={className}
                />
            </div>
            {dropdown}
        </>
    );
};

export default AutocompleteInput;
